(() => {
  if (customElements.get('configurable-banner-section')) return;

class ConfigurableBannerSection extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-banner-track]');
    if (!this.track || !this.track.classList.contains('configurable-banner-section__items--carousel')) return;

    this.controls = this.querySelector('[data-banner-controls]');
    this.progressBar = this.querySelector('[data-banner-progress-bar]');
    this.previousButton = this.querySelector('[data-banner-prev]');
    this.nextButton = this.querySelector('[data-banner-next]');

    this.onScroll = this.updateControls.bind(this);
    this.onResize = this.updateControls.bind(this);
    this.onPrevious = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onPointerDown = this.startDrag.bind(this);
    this.onPointerMove = this.drag.bind(this);
    this.onPointerUp = this.endDrag.bind(this);
    this.onClick = this.stopClickAfterDrag.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
    this.track.addEventListener('lostpointercapture', this.onPointerUp);
    this.track.addEventListener('click', this.onClick, true);
    window.addEventListener('resize', this.onResize);
    this.previousButton?.addEventListener('click', this.onPrevious);
    this.nextButton?.addEventListener('click', this.onNext);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
    }

    this.updateControls();
    requestAnimationFrame(() => this.updateControls());
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('pointermove', this.onPointerMove);
    this.track?.removeEventListener('pointerup', this.onPointerUp);
    this.track?.removeEventListener('pointercancel', this.onPointerUp);
    this.track?.removeEventListener('lostpointercapture', this.onPointerUp);
    this.track?.removeEventListener('click', this.onClick, true);
    window.removeEventListener('resize', this.onResize);
    this.previousButton?.removeEventListener('click', this.onPrevious);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.resizeObserver?.disconnect();
  }

  scrollByPage(direction) {
    const firstItem = this.track.querySelector('.configurable-banner-section__item');
    if (!firstItem) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll <= 0) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap || 0) || 0;
    const itemWidth = firstItem.getBoundingClientRect().width;
    const itemStep = itemWidth + gap;
    const visibleItems = itemStep > 0
      ? Math.max(1, Math.floor((this.track.clientWidth + gap) / itemStep))
      : 1;
    const amount = itemStep * visibleItems;
    const target = Math.max(0, Math.min(this.track.scrollLeft + (direction * amount), maxScroll));

    this.track.scrollTo({
      left: target,
      behavior: 'smooth'
    });
  }

  startDrag(event) {
    if (event.pointerType === 'touch' || (event.pointerType === 'mouse' && event.button !== 0)) return;

    this.isDragging = true;
    this.hasDragged = false;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.track.scrollLeft;
    this.dragPointerId = event.pointerId;
  }

  drag(event) {
    if (!this.isDragging) return;

    const deltaX = event.clientX - this.dragStartX;

    if (Math.abs(deltaX) > 3) {
      if (!this.hasDragged) {
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture(event.pointerId);
      }

      this.hasDragged = true;
      event.preventDefault();
      this.track.scrollLeft = this.dragStartScrollLeft - deltaX;
      this.updateControls();
    }
  }

  endDrag(event) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.classList.remove('is-dragging');

    if (this.track.hasPointerCapture?.(this.dragPointerId)) {
      this.track.releasePointerCapture(this.dragPointerId);
    }

    this.dragPointerId = null;

    requestAnimationFrame(() => this.updateControls());
  }

  stopClickAfterDrag(event) {
    if (!this.hasDragged) return;

    event.preventDefault();
    event.stopPropagation();
    this.hasDragged = false;
  }

  updateControls() {
    if (!this.track) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const canScroll = maxScroll > 1;

    if (this.controls) {
      this.controls.hidden = !canScroll;
    }

    if (this.progressBar) {
      const visibleRatio = canScroll ? this.track.clientWidth / this.track.scrollWidth : 1;
      const initialWidth = Math.max(visibleRatio * 100, 8);
      const progress = canScroll ? Math.max(0, Math.min(1, this.track.scrollLeft / maxScroll)) : 1;
      const barWidth = initialWidth + (100 - initialWidth) * progress;

      this.progressBar.style.width = `${barWidth}%`;
      this.progressBar.style.left = '0';
    }

    if (this.previousButton) {
      this.previousButton.disabled = !canScroll || this.track.scrollLeft <= 1;
    }

    if (this.nextButton) {
      this.nextButton.disabled = !canScroll || this.track.scrollLeft >= maxScroll - 1;
    }
  }
}

customElements.define('configurable-banner-section', ConfigurableBannerSection);
})();
